import { SparkIcon } from '../SparkIcon';
import { DropdownCheckIcon } from '../Dropdown/DropdownCheckIcon';
import { ReadOnlyTooltip, useReadOnlyPause } from '../ReadOnly/ReadOnly';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import './PopoverMenu.css';

export type PopoverMenuItemState = 'default' | 'hover' | 'selected' | 'disabled';
export type PopoverMenuItemType = 'regular' | 'cascading' | 'two-lines';

/** Single row inside a PopoverMenu. Supports regular, cascading, and two-line item types. */
export type PopoverMenuItemProps = {
  label?: string;
  title?: string;
  type?: PopoverMenuItemType;
  state?: PopoverMenuItemState;
  /**
   * Storybook-only visual state override for static matrices.
   */
  visualState?: PopoverMenuItemState;
  showPrefixIcon?: boolean;
  /** Icon slug for the prefix icon (from the Spark icon set). Defaults to "user". */
  prefixIconSlug?: string;
  showInfoIcon?: boolean;
  /** Tooltip on the trailing info icon (Retrieval Agent, etc.). */
  infoText?: string;
  /** Trailing icon slug (e.g. "eye" on Query File language rows). */
  suffixIconSlug?: string;
  className?: string;
  /** Disable this item while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  disableWhenReadOnly?: boolean;
  onClick?: () => void;
};

function InfoIcon({ text }: { text?: string }) {
  const icon = <SparkIcon slug="circle-info" size="small" className="spark-popover-menu-item-info" />;
  return (
    <span
      className="spark-popover-menu-item-info-wrap"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {text ? <TooltipTrigger text={text}>{icon}</TooltipTrigger> : icon}
    </span>
  );
}

export function PopoverMenuItem({
  label = 'Popover item text',
  title = 'Title',
  type = 'regular',
  state = 'default',
  visualState,
  showPrefixIcon = false,
  prefixIconSlug = 'user',
  showInfoIcon = false,
  infoText,
  suffixIconSlug,
  className = '',
  disableWhenReadOnly,
  onClick,
}: PopoverMenuItemProps) {
  const { paused, reason } = useReadOnlyPause(disableWhenReadOnly);
  const resolvedState = paused ? 'disabled' : visualState ?? state;
  const isDisabled = resolvedState === 'disabled';
  const isSelected = resolvedState === 'selected';

  const rootClasses = [
    'spark-popover-menu-item',
    `spark-popover-menu-item-${type}`,
    resolvedState === 'hover' ? 'spark-popover-menu-item-hover' : '',
    resolvedState === 'selected' ? 'spark-popover-menu-item-selected' : '',
    isDisabled ? 'spark-popover-menu-item-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const row =
    type === 'two-lines' ? (
      <div
        className={rootClasses}
        role="menuitem"
        aria-disabled={isDisabled || undefined}
        aria-selected={isSelected || undefined}
        onClick={isDisabled ? undefined : onClick}
      >
        <div className="spark-popover-menu-item-two-lines">
          <div className="spark-popover-menu-item-title-row">
            {showPrefixIcon ? (
              <SparkIcon slug={prefixIconSlug} size="small" className="spark-popover-menu-item-prefix" />
            ) : null}
            <span className="spark-popover-menu-item-title">{title}</span>
          </div>
          <div className="spark-popover-menu-item-subtitle-row">
            <span className="spark-popover-menu-item-subtitle">{label}</span>
            {showInfoIcon ? <InfoIcon text={infoText} /> : null}
          </div>
        </div>
        {isSelected ? <DropdownCheckIcon className="spark-popover-menu-item-check" /> : null}
      </div>
    ) : (
      <div
        className={rootClasses}
        role="menuitem"
        aria-disabled={isDisabled || undefined}
        aria-selected={isSelected || undefined}
        onClick={isDisabled ? undefined : onClick}
      >
        <div className="spark-popover-menu-item-content">
          {showPrefixIcon ? (
            <SparkIcon slug={prefixIconSlug} size="small" className="spark-popover-menu-item-prefix" />
          ) : null}
          <span className="spark-popover-menu-item-label">{label}</span>
        </div>
        {isSelected && type === 'regular' ? (
          <DropdownCheckIcon className="spark-popover-menu-item-check" />
        ) : null}
        {showInfoIcon ? <InfoIcon text={infoText} /> : null}
        {suffixIconSlug ? (
          <SparkIcon slug={suffixIconSlug} size="small" className="spark-popover-menu-item-suffix" />
        ) : null}
        {type === 'cascading' ? (
          <SparkIcon slug="chevron-right" size="small" className="spark-popover-menu-item-chevron" />
        ) : null}
      </div>
    );

  return (
    <ReadOnlyTooltip paused={paused} reason={reason} block placement="left">
      {row}
    </ReadOnlyTooltip>
  );
}
